import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, ShieldCheck, Eye, Lock, Timer } from "lucide-react";
import { pageLinks } from "@/lib/locale-alternates";
import { breadcrumb, graph, webPage, ORG_ID, WEBSITE_ID, TRAILS } from "@/lib/schema";
import { socialMeta } from "@/lib/seo";
import { PageHero, SectionHeading, FeatureCard } from "@/components/site/Blocks";
import { IdocDemoConsole } from "@/components/site/IdocDemoConsole";
import { getIdocmonDemoSnapshot } from "@/lib/idocmon-demo.functions";
import { SapConnectionForm } from "@/components/site/SapConnectionForm";
import { TrackRecordSection } from "@/components/site/TrackRecordSection";

const title = "AI IDOCMON Demo — Live IDoc Dashboard & SAP Connection | OXORY";
const description =
  "Try the live AI IDOCMON demo instance: a real IDoc monitoring dashboard with AI root-cause analysis on an anonymised subset of SAP IDoc traffic, plus a read-only connection request for your own system.";

export const Route = createFileRoute("/solutions_/ai-idocmon_/demo")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      ...socialMeta({
        title,
        description,
        path: "/solutions/ai-idocmon/demo",
        socialTitle: "AI IDOCMON Demo — live IDoc dashboard and SAP connection request",
        imageAlt: "AI IDOCMON demo dashboard showing failed and pending SAP IDocs",
      }),
    ],
    links: pageLinks("/solutions/ai-idocmon/demo"),
    scripts: [
      graph(
        { "@id": ORG_ID },
        { "@id": WEBSITE_ID },
        webPage({ path: "/solutions/ai-idocmon/demo", name: title, description }),
        breadcrumb(TRAILS["/solutions/ai-idocmon/demo"]),
      ),
    ],
  }),
  loader: () => getIdocmonDemoSnapshot(),
  errorComponent: () => (
    <div className="section-shell py-20">
      <h1 className="text-2xl font-bold">The demo instance is temporarily unavailable</h1>
      <p className="mt-3 text-sm text-muted-foreground">
        Please reload the page or contact OXORY for a guided session.
      </p>
    </div>
  ),
  component: AiIdocmonDemoPage,
});

const safeguards: Array<[string, string, typeof ShieldCheck]> = [
  [
    "Read-only by default",
    "The demo user only needs display authorisation for the IDoc monitor. Nothing is posted or reprocessed unless you explicitly enable it.",
    Eye,
  ],
  [
    "Your permissions, your control",
    "A fix runs after human approval by default. Authorised owners may separately enable autonomous correction for confirmed recurring patterns within a defined permission scope.",
    ShieldCheck,
  ],
  [
    "Masked payloads",
    "Personal and commercially sensitive fields are masked before an IDoc is analysed.",
    Lock,
  ],
  [
    "Time-boxed access",
    "Demo access expires automatically after the agreed window — usually two weeks.",
    Timer,
  ],
];

const steps: Array<[string, string, string]> = [
  [
    "01",
    "Send the form",
    "Tell us the system type and how you want to connect. No credentials in the form.",
  ],
  [
    "02",
    "Connection checklist",
    "You get a one-page checklist: RFC user, authorisations, network path, data-masking scope.",
  ],
  [
    "03",
    "Live session",
    "We run AI IDOCMON against your IDoc traffic and walk through the findings together.",
  ],
];

function AiIdocmonDemoPage() {
  const snapshot = Route.useLoaderData();
  return (
    <>
      <PageHero
        trail={TRAILS["/solutions/ai-idocmon/demo"]}
        eyebrow="AI IDOCMON demo"
        title="See IDoc errors explained before you connect anything"
        intro="Explore a running demo instance loaded with a safe, anonymised subset of SAP IDoc traffic, then request a read-only connection to your own system for a session against your real interfaces."
      />

      <section className="section-shell py-16">
        <SectionHeading
          eyebrow="Demo instance"
          title="A real IDOCMON instance, running on a safe subset of IDoc traffic"
        />
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground">
          This is not a mock-up: the dashboard below is served live from a running AI IDOCMON demo
          instance holding an anonymised subset of SAP IDoc traffic — masked partners, masked
          document numbers, no payload segments and no personal data. Select an IDoc to see how the
          engine explains the failure and which fix it proposes.
        </p>
        <div className="mt-10">
          <IdocDemoConsole snapshot={snapshot} />
        </div>
      </section>

      <section className="section-shell grid gap-12 pb-20 lg:grid-cols-[1fr_0.9fr]">
        <SapConnectionForm />
        <div>
          <SectionHeading
            eyebrow="How the demo works"
            title="From form to live session in three steps"
          />
          <ol className="mt-8 space-y-6">
            {steps.map(([number, heading, body]) => (
              <li key={number} className="rounded-lg border border-border bg-card p-6 shadow-card">
                <span className="font-mono text-xs font-bold text-primary">{number}</span>
                <h3 className="mt-2 text-base font-bold">{heading}</h3>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="section-shell pb-20">
        <SectionHeading eyebrow="Safeguards" title="What connecting a system actually means" />
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {safeguards.map(([heading, body, Icon]) => (
            <FeatureCard key={heading} title={heading}>
              <Icon className="mb-3 size-5 text-primary" aria-hidden="true" />
              {body}
            </FeatureCard>
          ))}
        </div>
      </section>

      <section className="section-shell pb-20">
        <div className="rounded-lg border border-border bg-card p-10 shadow-card">
          <h2 className="text-2xl font-bold md:text-3xl">Prefer to talk it through first?</h2>
          <p className="mt-3 max-w-xl text-sm text-muted-foreground">
            We are happy to walk through the solution, the security model and a realistic scope
            before anyone touches a system.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:bg-primary/90"
            >
              Contact OXORY <ArrowRight className="size-4" />
            </Link>
            <Link
              to="/solutions/ai-idocmon"
              className="inline-flex items-center gap-2 rounded-md border border-border px-5 py-3 text-sm font-bold hover:bg-secondary"
            >
              Back to AI IDOCMON
            </Link>
          </div>
        </div>
        <p className="mt-6 text-xs leading-relaxed text-muted-foreground">
          SAP and IDoc are registered trademarks of SAP SE. AI IDOCMON is an independent OXORY
          solution and has no partnership or affiliation with SAP SE.
        </p>
      </section>
      <TrackRecordSection locale="en" />
    </>
  );
}
